import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isBridgeRequest,
  type BridgeResponse,
} from './bridge-protocol.js';
import { createMockResult } from './mock-host.js';

interface HostLog {
  id: string;
  method: string;
  time: string;
}

export function MockShell() {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [logs, setLogs] = useState<HostLog[]>([]);

  const sendReady = useCallback((): void => {
    frameRef.current?.contentWindow?.postMessage(
      {
        type: 'bridge-event',
        event: 'TribeHostReady',
        payload: { hostVersion: 'mock-0.8' },
      },
      window.location.origin,
    );
  }, []);

  useEffect(() => {
    const onMessage = (message: MessageEvent<unknown>): void => {
      if (
        message.source !== frameRef.current?.contentWindow ||
        message.origin !== window.location.origin ||
        !isBridgeRequest(message.data)
      ) {
        return;
      }

      const request = message.data;

      setLogs((current) =>
        [
          {
            id: crypto.randomUUID(),
            method: request.method,
            time: new Date().toLocaleTimeString(),
          },
          ...current,
        ].slice(0, 12),
      );

      try {
        const response: BridgeResponse = {
          type: 'bridge',
          callId: request.callId,
          result: createMockResult(request.method, request.params),
        };
        frameRef.current?.contentWindow?.postMessage(
          response,
          window.location.origin,
        );
      } catch (error) {
        const response: BridgeResponse = {
          type: 'bridge',
          callId: request.callId,
          error: {
            code: 'MOCK_HOST_ERROR',
            message:
              error instanceof Error ? error.message : 'Mock host failed.',
          },
        };
        frameRef.current?.contentWindow?.postMessage(
          response,
          window.location.origin,
        );
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <main className="mock-shell">
      <aside className="mock-panel">
        <div>
          <div className="brand-mark" aria-hidden="true">
            ↔
          </div>
          <p className="eyebrow">Standalone harness</p>
          <h1>Tribe SDK Lab</h1>
          <p className="mock-intro">
            Every SDK call travels through a real nested iframe. This page
            supplies deterministic mock host responses; the App Wrapper swaps
            it for the live Tribe host without changing the tester.
          </p>
        </div>

        <div className="wire">
          <span>Mock host</span>
          <i aria-hidden="true" />
          <span>SDK iframe</span>
        </div>

        <section className="host-log" aria-label="Recent mock host calls">
          <div className="section-heading">
            <span>Host traffic</span>
            <strong>{logs.length}</strong>
          </div>
          {logs.length === 0 ? (
            <p className="empty-copy">Run a capability to see bridge calls.</p>
          ) : (
            <ol>
              {logs.map((entry) => (
                <li key={entry.id}>
                  <code>{entry.method.replace('TribeApp', '')}</code>
                  <time>{entry.time}</time>
                </li>
              ))}
            </ol>
          )}
        </section>

        <a
          className="source-link"
          href="https://github.com/bakulev/werreal-wrapped-test"
          rel="noreferrer"
          target="_blank"
        >
          View source on GitHub
        </a>
      </aside>

      <section className="mock-stage">
        <iframe
          ref={frameRef}
          allow="clipboard-write"
          onLoad={sendReady}
          sandbox="allow-forms allow-modals allow-popups allow-same-origin allow-scripts"
          src="/app?lab_mode=mock"
          title="Tribe SDK tester"
        />
      </section>
    </main>
  );
}
